import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { ChevronLeft, ChevronRight, Minus, Plus, RotateCcw, X } from 'lucide-react';
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import type { ScatterShapeProps } from 'recharts';
import { getApiErrorMessage } from '../../lib/api';
import type { DashboardDailyBAPresenceDetail } from '../../types/domain';

const fullDateFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Asia/Karachi',
});

type PresenceMapPoint = {
  brand_ambassadors: DashboardDailyBAPresenceDetail['stores'][number]['brand_ambassadors'];
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  name: string;
  radius: number;
  selectedDate: string;
  status: DashboardDailyBAPresenceDetail['stores'][number]['status'];
};

type PresenceStatus = PresenceMapPoint['status'];

type PresenceTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
};

const MIN_MAP_ZOOM = 1;
const MAX_MAP_ZOOM = 6;
const MAP_ZOOM_STEP = 1.5;
const MAP_CHART_MARGIN = { top: 48, right: 22, bottom: 22, left: 22 };

type MapViewport = {
  latitude: number;
  longitude: number;
  zoom: number;
};

type MapCoordinate = Pick<MapViewport, 'latitude' | 'longitude'>;

type MapDrag = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  viewport: MapViewport;
};

const presenceStatusDetails: Record<PresenceStatus, { colour: string; emptyLabel: string; label: string }> = {
  present: {
    colour: '#2f8065',
    emptyLabel: 'BA present',
    label: 'BA present',
  },
  scheduled_no_effective_checkin: {
    colour: '#c65a57',
    emptyLabel: 'Scheduled, no effective check-in',
    label: 'Scheduled, no check-in',
  },
  not_scheduled: {
    colour: '#738079',
    emptyLabel: 'No shift scheduled',
    label: 'No shift scheduled',
  },
};

const presenceStatusOrder: PresenceStatus[] = [
  'present',
  'scheduled_no_effective_checkin',
  'not_scheduled',
];

function clampMapZoom(value: number) {
  return Math.min(MAX_MAP_ZOOM, Math.max(MIN_MAP_ZOOM, value));
}

function formatDate(value: string) {
  return fullDateFormatter.format(new Date(`${value}T12:00:00+05:00`));
}

function offsetDate(value: string, days: number) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function isPresenceStatus(value: unknown): value is PresenceStatus {
  return value === 'present'
    || value === 'scheduled_no_effective_checkin'
    || value === 'not_scheduled';
}

function isPresenceMapPoint(value: unknown): value is PresenceMapPoint {
  return Boolean(
    value
      && typeof value === 'object'
      && 'brand_ambassadors' in value
      && 'name' in value
      && 'selectedDate' in value
      && 'status' in value
      && isPresenceStatus(value.status),
  );
}

function axisDomain(values: number[]): [number, number] {
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const span = Math.max(maximum - minimum, 0.002);
  const padding = span * 0.16;
  return [minimum - padding, maximum + padding];
}

function clampViewportCentre(domain: [number, number], value: number, zoom: number) {
  const [minimum, maximum] = domain;
  const halfSpan = (maximum - minimum) / (zoom * 2);
  return Math.min(maximum - halfSpan, Math.max(minimum + halfSpan, value));
}

function viewportAxisDomain(
  domain: [number, number],
  centre: number,
  zoom: number,
): [number, number] {
  const clampedCentre = clampViewportCentre(domain, centre, zoom);
  const [minimum, maximum] = domain;
  const halfSpan = (maximum - minimum) / (zoom * 2);
  return [clampedCentre - halfSpan, clampedCentre + halfSpan];
}

function mapCoordinateAtPointer(
  map: HTMLElement,
  clientX: number,
  clientY: number,
  longitudeDomain: [number, number],
  latitudeDomain: [number, number],
): MapCoordinate {
  const bounds = map.getBoundingClientRect();
  const plotWidth = Math.max(1, bounds.width - MAP_CHART_MARGIN.left - MAP_CHART_MARGIN.right);
  const plotHeight = Math.max(1, bounds.height - MAP_CHART_MARGIN.top - MAP_CHART_MARGIN.bottom);
  const horizontalPosition = Math.min(
    1,
    Math.max(0, (clientX - bounds.left - MAP_CHART_MARGIN.left) / plotWidth),
  );
  const verticalPosition = Math.min(
    1,
    Math.max(0, (clientY - bounds.top - MAP_CHART_MARGIN.top) / plotHeight),
  );

  return {
    latitude: latitudeDomain[1] - verticalPosition * (latitudeDomain[1] - latitudeDomain[0]),
    longitude: longitudeDomain[0] + horizontalPosition * (longitudeDomain[1] - longitudeDomain[0]),
  };
}

function zoomViewport(
  viewport: MapViewport,
  fullLongitudeDomain: [number, number],
  fullLatitudeDomain: [number, number],
  multiplier: number,
  anchor?: MapCoordinate,
): MapViewport {
  const zoom = clampMapZoom(viewport.zoom * multiplier);
  if (zoom === viewport.zoom) return viewport;

  const currentLongitudeDomain = viewportAxisDomain(
    fullLongitudeDomain,
    viewport.longitude,
    viewport.zoom,
  );
  const currentLatitudeDomain = viewportAxisDomain(
    fullLatitudeDomain,
    viewport.latitude,
    viewport.zoom,
  );
  const longitude = anchor?.longitude ?? (currentLongitudeDomain[0] + currentLongitudeDomain[1]) / 2;
  const latitude = anchor?.latitude ?? (currentLatitudeDomain[0] + currentLatitudeDomain[1]) / 2;
  const longitudePosition = (longitude - currentLongitudeDomain[0])
    / (currentLongitudeDomain[1] - currentLongitudeDomain[0]);
  const latitudePosition = (latitude - currentLatitudeDomain[0])
    / (currentLatitudeDomain[1] - currentLatitudeDomain[0]);
  const longitudeSpan = (fullLongitudeDomain[1] - fullLongitudeDomain[0]) / zoom;
  const latitudeSpan = (fullLatitudeDomain[1] - fullLatitudeDomain[0]) / zoom;

  return {
    latitude: clampViewportCentre(
      fullLatitudeDomain,
      latitude + (0.5 - latitudePosition) * latitudeSpan,
      zoom,
    ),
    longitude: clampViewportCentre(
      fullLongitudeDomain,
      longitude + (0.5 - longitudePosition) * longitudeSpan,
      zoom,
    ),
    zoom,
  };
}

function withVisibleLabels(points: PresenceMapPoint[]): PresenceMapPoint[] {
  if (!points.length) return points;

  const [minimumLongitude, maximumLongitude] = axisDomain(points.map((point) => point.longitude));
  const [minimumLatitude, maximumLatitude] = axisDomain(points.map((point) => point.latitude));
  const longitudeSpan = maximumLongitude - minimumLongitude;
  const latitudeSpan = maximumLatitude - minimumLatitude;
  const occupied: Array<{ bottom: number; left: number; right: number; top: number }> = [];
  const labelsById = new Set<string>();

  for (const point of [...points].sort((left, right) => (
    right.brand_ambassadors.length - left.brand_ambassadors.length
    || left.name.localeCompare(right.name)
  ))) {
    const centreX = ((point.longitude - minimumLongitude) / longitudeSpan) * 100;
    const centreY = (1 - ((point.latitude - minimumLatitude) / latitudeSpan)) * 100;
    const width = Math.min(31, Math.max(10, point.name.length * 0.82));
    const box = {
      bottom: centreY - 2,
      left: centreX - width / 2,
      right: centreX + width / 2,
      top: centreY - 8,
    };
    const overlaps = occupied.some((other) => (
      box.left < other.right
      && box.right > other.left
      && box.top < other.bottom
      && box.bottom > other.top
    ));
    if (!overlaps) {
      occupied.push(box);
      labelsById.add(point.id);
    }
  }

  return points.map((point) => ({
    ...point,
    label: labelsById.has(point.id) ? point.name : '',
  }));
}

function presenceMapPoints(
  stores: DashboardDailyBAPresenceDetail['stores'],
  selectedDate: string,
): PresenceMapPoint[] {
  const groupedStores = new Map<string, DashboardDailyBAPresenceDetail['stores']>();
  for (const store of stores) {
    const latitude = Number(store.latitude);
    const longitude = Number(store.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
    const coordinateKey = `${latitude.toFixed(4)}:${longitude.toFixed(4)}`;
    const coordinateGroup = groupedStores.get(coordinateKey) ?? [];
    coordinateGroup.push(store);
    groupedStores.set(coordinateKey, coordinateGroup);
  }

  const points: PresenceMapPoint[] = [];
  for (const coordinateGroup of groupedStores.values()) {
    const orderedGroup = [...coordinateGroup].sort((left, right) => left.name.localeCompare(right.name));
    const separation = orderedGroup.length > 1 ? 0.00008 : 0;
    orderedGroup.forEach((store, index) => {
      const angle = orderedGroup.length > 1
        ? (Math.PI * 2 * index) / orderedGroup.length - Math.PI / 2
        : 0;
      const activeCount = store.brand_ambassadors.length;
      points.push({
        brand_ambassadors: [...store.brand_ambassadors].sort((left, right) => left.name.localeCompare(right.name)),
        id: store.id,
        label: '',
        latitude: Number(store.latitude) + Math.sin(angle) * separation,
        longitude: Number(store.longitude) + Math.cos(angle) * separation,
        name: store.name,
        radius: Math.min(24, 8 + Math.sqrt(Math.max(activeCount - 1, 0)) * 6),
        selectedDate,
        status: store.status,
      });
    });
  }

  return withVisibleLabels(points.sort((left, right) => left.name.localeCompare(right.name)));
}

export function DailyBAPresenceTooltip({ active, payload }: PresenceTooltipProps) {
  const store = payload?.[0]?.payload;
  if (!active || !isPresenceMapPoint(store)) return null;
  const status = presenceStatusDetails[store.status];

  return (
    <div className="daily-ba-presence-tooltip">
      <strong>{store.name}</strong>
      <span>{formatDate(store.selectedDate)}</span>
      <span>{store.brand_ambassadors.length
        ? store.brand_ambassadors.map((ambassador) => ambassador.name).join(' · ')
        : status.emptyLabel}
      </span>
    </div>
  );
}

function StorePresencePoint({ cx, cy, payload }: ScatterShapeProps) {
  if (!isPresenceMapPoint(payload) || cx === undefined || cy === undefined) return null;
  const colour = presenceStatusDetails[payload.status].colour;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        fill={colour}
        fillOpacity={0.2}
        r={payload.radius + 3}
      />
      <circle cx={cx} cy={cy} fill={colour} r={payload.radius} />
      {payload.label ? (
        <text
          className="daily-ba-presence-store-label"
          pointerEvents="none"
          textAnchor="middle"
          x={cx}
          y={cy - payload.radius - 8}
        >
          {payload.label}
        </text>
      ) : null}
    </g>
  );
}

export function DailyBAPresenceModal({
  detail,
  error,
  isLoading,
  maxDate,
  minDate,
  onClose,
  onDateChange,
  selectedDate,
}: {
  detail: DashboardDailyBAPresenceDetail | undefined;
  error: unknown;
  isLoading: boolean;
  maxDate: string;
  minDate: string;
  onClose: () => void;
  onDateChange: (date: string) => void;
  selectedDate: string;
}) {
  const mapRef = useRef<HTMLElement>(null);
  const dragRef = useRef<MapDrag | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [viewport, setViewport] = useState<MapViewport>({
    latitude: 0,
    longitude: 0,
    zoom: MIN_MAP_ZOOM,
  });
  const points = presenceMapPoints(detail?.stores ?? [], selectedDate);
  const fullLongitudeDomain = points.length ? axisDomain(points.map((point) => point.longitude)) : [0, 1] as [number, number];
  const fullLatitudeDomain = points.length ? axisDomain(points.map((point) => point.latitude)) : [0, 1] as [number, number];
  const longitudeDomain = viewportAxisDomain(fullLongitudeDomain, viewport.longitude, viewport.zoom);
  const latitudeDomain = viewportAxisDomain(fullLatitudeDomain, viewport.latitude, viewport.zoom);
  const mapStateRef = useRef<{
    fullLatitudeDomain: [number, number];
    fullLongitudeDomain: [number, number];
    latitudeDomain: [number, number];
    longitudeDomain: [number, number];
  }>({
    fullLatitudeDomain: [0, 1],
    fullLongitudeDomain: [0, 1],
    latitudeDomain: [0, 1],
    longitudeDomain: [0, 1],
  });
  useEffect(() => {
    mapStateRef.current = {
      fullLatitudeDomain,
      fullLongitudeDomain,
      latitudeDomain,
      longitudeDomain,
    };
  });
  const canZoomIn = viewport.zoom < MAX_MAP_ZOOM;
  const canZoomOut = viewport.zoom > MIN_MAP_ZOOM;
  const presenceCounts: Record<PresenceStatus, number> = {
    not_scheduled: 0,
    present: 0,
    scheduled_no_effective_checkin: 0,
  };
  for (const point of points) presenceCounts[point.status] += 1;
  const resetMapView = () => {
    dragRef.current = null;
    setIsDragging(false);
    setViewport({ latitude: 0, longitude: 0, zoom: MIN_MAP_ZOOM });
  };
  const changeZoom = (multiplier: number, anchor?: MapCoordinate) => {
    setViewport((current) => zoomViewport(
      current,
      fullLongitudeDomain,
      fullLatitudeDomain,
      multiplier,
      anchor,
    ));
  };
  const changeDate = (date: string) => {
    resetMapView();
    onDateChange(date);
  };

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !canZoomOut) return;
    if (event.target instanceof Element && event.target.closest('.daily-ba-presence-map-controls')) return;

    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      viewport,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const moveDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const plotWidth = Math.max(1, bounds.width - MAP_CHART_MARGIN.left - MAP_CHART_MARGIN.right);
    const plotHeight = Math.max(1, bounds.height - MAP_CHART_MARGIN.top - MAP_CHART_MARGIN.bottom);
    const dragLongitudeDomain = viewportAxisDomain(
      fullLongitudeDomain,
      drag.viewport.longitude,
      drag.viewport.zoom,
    );
    const dragLatitudeDomain = viewportAxisDomain(
      fullLatitudeDomain,
      drag.viewport.latitude,
      drag.viewport.zoom,
    );
    const longitudeOffset = -((event.clientX - drag.startClientX) / plotWidth)
      * (dragLongitudeDomain[1] - dragLongitudeDomain[0]);
    const latitudeOffset = ((event.clientY - drag.startClientY) / plotHeight)
      * (dragLatitudeDomain[1] - dragLatitudeDomain[0]);

    event.preventDefault();
    setViewport({
      latitude: clampViewportCentre(
        fullLatitudeDomain,
        drag.viewport.latitude + latitudeOffset,
        drag.viewport.zoom,
      ),
      longitude: clampViewportCentre(
        fullLongitudeDomain,
        drag.viewport.longitude + longitudeOffset,
        drag.viewport.zoom,
      ),
      zoom: drag.viewport.zoom,
    });
  };

  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const handleWheel = (event: WheelEvent) => {
      if (!event.deltaY) return;
      event.preventDefault();
      const state = mapStateRef.current;
      const anchor = mapCoordinateAtPointer(
        map,
        event.clientX,
        event.clientY,
        state.longitudeDomain,
        state.latitudeDomain,
      );
      setViewport((current) => zoomViewport(
        current,
        state.fullLongitudeDomain,
        state.fullLatitudeDomain,
        Math.exp(-event.deltaY * 0.0025),
        anchor,
      ));
    };

    map.addEventListener('wheel', handleWheel, { passive: false });
    return () => map.removeEventListener('wheel', handleWheel);
  }, [points.length]);

  return (
    <div className="confirmation-backdrop daily-ba-presence-backdrop" role="presentation">
      <section aria-labelledby="daily-ba-presence-title" aria-modal="true" className="confirmation-dialog daily-ba-presence-dialog" role="dialog">
        <button aria-label="Close daily BA presence" className="icon-button confirmation-close" onClick={onClose} type="button">
          <X size={19} />
        </button>
        <header className="daily-ba-presence-heading">
          <div>
            <p className="eyebrow">Daily BA presence</p>
            <h2 id="daily-ba-presence-title">Store presence</h2>
            <p>Campaign-store coverage on {formatDate(selectedDate)}.</p>
          </div>
          <div className="daily-ba-presence-date-controls">
            <button
              aria-label="Previous day"
              className="icon-button"
              disabled={selectedDate <= minDate}
              onClick={() => changeDate(offsetDate(selectedDate, -1))}
              type="button"
            >
              <ChevronLeft size={18} />
            </button>
            <label>
              Date
              <input
                aria-label="Daily BA presence date"
                max={maxDate}
                min={minDate}
                onChange={(event) => changeDate(event.target.value)}
                type="date"
                value={selectedDate}
              />
            </label>
            <button
              aria-label="Next day"
              className="icon-button"
              disabled={selectedDate >= maxDate}
              onClick={() => changeDate(offsetDate(selectedDate, 1))}
              type="button"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </header>

        {isLoading ? (
          <div aria-live="polite" className="store-sales-detail-loading">
            <span className="button-spinner" /> Loading store presence…
          </div>
        ) : error ? (
          <div className="store-sales-detail-loading">
            <p className="form-error" role="alert">{getApiErrorMessage(error)}</p>
          </div>
        ) : points.length ? (
          <>
            <div aria-label="Store presence legend" className="daily-ba-presence-legend">
              {presenceStatusOrder.map((status) => (
                <span key={status}>
                  <i aria-hidden="true" style={{ background: presenceStatusDetails[status].colour }} />
                  {presenceStatusDetails[status].label} ({presenceCounts[status]})
                </span>
              ))}
            </div>
            <section
              aria-label="Store presence map"
              className={`daily-ba-presence-map${canZoomOut ? ' is-pannable' : ''}${isDragging ? ' is-panning' : ''}`}
              onPointerCancel={endDrag}
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              ref={mapRef}
            >
              <div aria-label={`Map zoom: ${Math.round(viewport.zoom * 100)}%`} className="daily-ba-presence-map-controls" role="group">
                <button
                  aria-label="Zoom out"
                  className="icon-button"
                  disabled={!canZoomOut}
                  onClick={() => changeZoom(1 / MAP_ZOOM_STEP)}
                  type="button"
                >
                  <Minus size={17} />
                </button>
                <span aria-hidden="true">{Math.round(viewport.zoom * 100)}%</span>
                <button
                  aria-label="Reset map zoom"
                  className="icon-button"
                  disabled={!canZoomOut}
                  onClick={resetMapView}
                  type="button"
                >
                  <RotateCcw size={15} />
                </button>
                <button
                  aria-label="Zoom in"
                  className="icon-button"
                  disabled={!canZoomIn}
                  onClick={() => changeZoom(MAP_ZOOM_STEP)}
                  type="button"
                >
                  <Plus size={17} />
                </button>
              </div>
              <ResponsiveContainer height="100%" width="100%">
                <ScatterChart accessibilityLayer={false} margin={MAP_CHART_MARGIN}>
                  <XAxis allowDataOverflow dataKey="longitude" domain={longitudeDomain} hide type="number" />
                  <YAxis allowDataOverflow dataKey="latitude" domain={latitudeDomain} hide type="number" />
                  <ZAxis dataKey="radius" range={[64, 576]} />
                  <CartesianGrid stroke="var(--muted)" strokeDasharray="3 5" strokeOpacity={0.55} />
                  <Tooltip content={<DailyBAPresenceTooltip />} cursor={false} />
                  <Scatter
                    data={points}
                    fill="var(--link)"
                    shape={(props) => <StorePresencePoint {...props} />}
                  />
                </ScatterChart>
              </ResponsiveContainer>
              <ul className="sr-only">
                {points.map((point) => (
                  <li key={point.id}>
                    {point.name}: {point.brand_ambassadors.length
                      ? point.brand_ambassadors.map((ambassador) => ambassador.name).join(', ')
                      : presenceStatusDetails[point.status].emptyLabel}
                  </li>
                ))}
              </ul>
            </section>
          </>
        ) : (
          <div className="empty-state compact store-sales-detail-empty">
            <h3>No campaign stores</h3>
            <p>No authorised campaign stores are available for this day.</p>
          </div>
        )}
      </section>
    </div>
  );
}
